import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { StringArrayPayload } from './config/arrayPayload.types.ts';
import { storeNames } from './config/config.type.ts';

type OrbitOneVisibilityMode = 'pen' | 'site';

interface OrbitOneVisibilityStore {
  visibilityMode: OrbitOneVisibilityMode;
  visiblePenIds: string[];
  initialized: boolean;
  setVisibilityMode: (mode: OrbitOneVisibilityMode) => void;
  setVisiblePenIds: (penIds: StringArrayPayload) => void;
  initialize: (penId: string) => void;
}

type PersistedOrbitOneVisibilityStore = Pick<
  OrbitOneVisibilityStore,
  'visibilityMode' | 'visiblePenIds' | 'initialized'
>;

export const useOrbitOneVisibilityStorage = create(
  persist<OrbitOneVisibilityStore, [], [], PersistedOrbitOneVisibilityStore>(
    set => ({
      visibilityMode: 'pen',
      visiblePenIds: [],
      initialized: false,
      setVisibilityMode: mode => set(() => ({ visibilityMode: mode })),
      setVisiblePenIds: penIdsOrUpdater =>
        set(state => ({
          visibilityMode: 'pen',
          visiblePenIds:
            typeof penIdsOrUpdater === 'function'
              ? penIdsOrUpdater(state.visiblePenIds)
              : penIdsOrUpdater,
        })),
      initialize: penId =>
        set(() => ({ visibilityMode: 'pen', visiblePenIds: [penId], initialized: true })),
    }),
    {
      name: storeNames.orbitOneVisibility,
      storage: createJSONStorage(() => localStorage),
      /** visibilityMode persists so a reload keeps the site view instead of falling back to a pen. */
      partialize: state => ({
        visibilityMode: state.visibilityMode,
        visiblePenIds: state.visiblePenIds,
        initialized: state.initialized,
      }),
    }
  )
);
